import React, { useCallback, useEffect, useState } from "react";
import { useDetectClickOutside } from "react-detect-click-outside";
import { FormattedMessage } from "react-intl";

import { Portal } from "@mui/material";

import { ChevronDownSquareCornersIcon20, ChevronDownSquareCornersIcon24 } from "@/components/icons";

import { DropdownFilterVariant } from "@/types/filters";

import {
    ArrowIconWrapper,
    Container,
    IconAndTitleWrapper,
    List,
    ListContainer,
    PopUp,
    Toggle,
} from "../filters.styles";

import { Checkbox } from "./components/checkbox/checkbox";
import { CheckboxContainer } from "./components/checkbox/checkbox.styles";

import type { MultiSelectDropdownProps, MultiSelectOption } from "./multi-select.types";

import {
    MultiSelectPopUpListItem,
    SelectAllContainer,
    SelectedCountSpan,
    SeparatorLine,
    TextSpan,
    TitleAndCountWrapper,
} from "./multi-select.styles";

export const MultiSelectDropdown = ({
    handleOnClick,
    onSelectionChange,
    options,
    title,
    icon,
    showSelectAll = false,
    singleSelect = false,
    variant = DropdownFilterVariant.PrimaryGrey800,
    initialSelectedOptions = [],
    fullWidth = false,
    popUpWidth,
    ...props
}: MultiSelectDropdownProps) => {
    const [selectedOptions, setSelectedOptions] =
        useState<MultiSelectOption[]>(initialSelectedOptions);
    const [isOpen, setIsOpen] = useState(false);
    //this state prevents the PopUp from jumping from its original position to the new one
    const [isPopUpReady, setIsPopUpReady] = useState(false);
    const [popUpStyle, setPopUpStyle] = useState<React.CSSProperties>({});

    //populate initial selected options
    useEffect(() => {
        setSelectedOptions(initialSelectedOptions);
    }, [initialSelectedOptions]);

    const handleToggleList = useCallback(() => {
        setIsOpen((currentIsOpen) => !currentIsOpen);
    }, []);

    const handleOptionToggle = useCallback(
        (option: MultiSelectOption) => {
            setSelectedOptions((prevSelected) => {
                if (singleSelect) {
                    const newSelected = [option];
                    onSelectionChange(newSelected);
                    setIsOpen(false);
                    return newSelected;
                }

                const isSelected = prevSelected.some(({ id }) => id === option.id);
                const newSelected = isSelected
                    ? prevSelected.filter(({ id }) => id !== option.id)
                    : [...prevSelected, option];

                onSelectionChange(newSelected);

                return newSelected;
            });
        },
        [onSelectionChange, singleSelect],
    );

    const handleSelectAll = useCallback(() => {
        if (singleSelect) return;
        const allSelected = options.length === selectedOptions.length;
        const newSelected = allSelected ? [] : options;

        setSelectedOptions(newSelected);
        onSelectionChange(newSelected);
    }, [options, selectedOptions, onSelectionChange, singleSelect]);

    const clickOutsideRef = useDetectClickOutside({
        onTriggered: () => {
            setIsOpen(false);
            handleOnClick(null);
        },
    });

    //define the PopUp position, in relation to the Toggle
    useEffect(() => {
        if (isOpen) {
            const toggleButton = document.getElementById("multi-select-toggle-button");

            if (toggleButton) {
                const rect = toggleButton.getBoundingClientRect();

                setPopUpStyle({
                    top: `${window.scrollY + rect.bottom + 8}px`, // Adjust position relative to viewport
                    right: `${document.documentElement.clientWidth - rect.right}px`,
                });

                setIsPopUpReady(true);
            } else {
                setIsPopUpReady(false);
            }
        }
    }, [isOpen]);

    return (
        <Container ref={clickOutsideRef} {...props}>
            <Toggle
                id="multi-select-toggle-button"
                isOpen={isOpen}
                variant={variant}
                fullWidth={fullWidth}
                onClick={handleToggleList}
            >
                <IconAndTitleWrapper>
                    {icon}
                    <TitleAndCountWrapper>
                        <span>{title}</span>
                        {!singleSelect && (
                            <SelectedCountSpan>{selectedOptions.length}</SelectedCountSpan>
                        )}
                    </TitleAndCountWrapper>
                </IconAndTitleWrapper>
                <ArrowIconWrapper isOpen={isOpen}>
                    {variant === DropdownFilterVariant.PrimaryGrey800 ? (
                        <ChevronDownSquareCornersIcon24 />
                    ) : (
                        <ChevronDownSquareCornersIcon20 />
                    )}
                </ArrowIconWrapper>
            </Toggle>

            {isOpen && isPopUpReady && (
                /* portal is needed to render PopUp outside the component */
                <Portal>
                    <PopUp
                        style={popUpStyle}
                        popUpWidth={popUpWidth}
                        variant={DropdownFilterVariant.PrimaryGrey800}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()} //prevents closing when clicking an option
                    >
                        <ListContainer variant={variant}>
                            {showSelectAll && !singleSelect && (
                                <SelectAllContainer
                                    isChecked={options.length === selectedOptions.length}
                                    onClick={handleSelectAll}
                                    variant={variant}
                                >
                                    <CheckboxContainer>
                                        <Checkbox
                                            checked={options.length === selectedOptions.length}
                                            onChange={handleSelectAll}
                                        />
                                    </CheckboxContainer>
                                    <TextSpan
                                        className={
                                            options.length === selectedOptions.length
                                                ? "checked"
                                                : ""
                                        }
                                    >
                                        <FormattedMessage id="filters:multi-select:select-all" />
                                    </TextSpan>
                                    <SeparatorLine />
                                </SelectAllContainer>
                            )}

                            <List>
                                {options.map((option) => {
                                    const isChecked = selectedOptions.some(({ id }) => id === option.id);
                                    return (
                                        <MultiSelectPopUpListItem
                                            key={option.id}
                                            variant={variant}
                                            isChecked={isChecked}
                                            onClick={() => handleOptionToggle(option)}
                                        >
                                            {!singleSelect && (
                                                <CheckboxContainer>
                                                    <Checkbox
                                                        checked={isChecked}
                                                        onChange={() => handleOptionToggle(option)}
                                                    />
                                                </CheckboxContainer>
                                            )}
                                            <TextSpan
                                                className={
                                                    isChecked ? "checked" : ""
                                                }
                                            >
                                                {option.label}
                                            </TextSpan>
                                        </MultiSelectPopUpListItem>
                                    );
                                })}
                            </List>
                        </ListContainer>
                    </PopUp>
                </Portal>
            )}
        </Container>
    );
};
